import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Icon } from '../../components/Icon';
import { viewForExtent } from '../../map/viewForExtent';
import type { MapView } from '../../map/model';
import type { SpatialExtent } from '../../data/model';
import { useAppStore } from '../state/useAppStore';
import { geoapifyAutocompleteUrl, parseGeoapifyPlaces, type PlaceResult } from '../search/geoapify';
import {
  extentForGeometry,
  parseDecimalCoordinates,
  searchProjectFeatures,
  type FeatureSearchResult,
} from '../search/searchDomain';

type SearchMode = 'Places' | 'Features' | 'Coordinates';
const modes: readonly SearchMode[] = ['Places', 'Features', 'Coordinates'];
export function MapSearch({
  open,
  onToggle,
  onDismiss,
}: {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onDismiss: () => void;
}) {
  const geoapifyKey = import.meta.env.VITE_GEOAPIFY_API_KEY?.trim() ?? '';
  const [mode, setMode] = useState<SearchMode>('Places');
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState<readonly PlaceResult[]>([]);
  const [features, setFeatures] = useState<readonly FeatureSearchResult[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completedQuery, setCompletedQuery] = useState('');
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const generation = useRef(0);
  const placeCache = useRef(new Map<string, readonly PlaceResult[]>());
  const sources = useAppStore((state) => state.project.sources);
  const layers = useAppStore((state) => state.project.layers);
  const view = useAppStore((state) => state.project.map.view);
  const chosen = useAppStore((state) => state.shell.searchTarget);
  const showTarget = useAppStore((state) => state.showSearchTarget);
  const centerRef = useRef(view.center);
  useEffect(() => {
    centerRef.current = chosen?.view.center ?? view.center;
  }, [chosen, view.center]);
  const coordinate = mode === 'Coordinates' ? parseDecimalCoordinates(query) : null;

  useLayoutEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const position = () => {
      const panel = panelRef.current;
      const button = buttonRef.current;
      const viewport = button?.closest<HTMLElement>('.map-viewport');
      if (!panel || !button || !viewport) return;
      const frame = viewport.getBoundingClientRect();
      const anchor = button.getBoundingClientRect();
      if (window.innerWidth <= 620) {
        panel.style.position = 'fixed';
        panel.style.left = '12px';
        panel.style.bottom = `${Math.max(12, window.innerHeight - anchor.top + 8)}px`;
        panel.style.width = `${window.innerWidth - 24}px`;
        panel.style.maxHeight = `${Math.max(150, anchor.top - 24)}px`;
        return;
      }
      panel.style.position = 'absolute';
      const width = Math.max(200, Math.min(352, frame.width - 24));
      panel.style.width = `${width}px`;
      panel.style.left = `${Math.max(0, Math.min(anchor.left - frame.left, frame.width - width - 12)) - (anchor.left - frame.left)}px`;
      panel.style.maxHeight = `${Math.max(150, anchor.top - frame.top - 20)}px`;
    };
    position();
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (
        panelRef.current?.contains(event.target as Node) ||
        buttonRef.current?.contains(event.target as Node)
      )
        return;
      onDismiss();
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open, onDismiss]);

  useEffect(() => {
    const revision = ++generation.current;
    const needle = query.trim();
    queueMicrotask(() => {
      if (revision !== generation.current) return;
      setActiveIndex(0);
      setError('');
      setPlaces([]);
      setFeatures([]);
      setHasMore(false);
      setCompletedQuery('');
      setLoading(
        open &&
          ((mode === 'Places' && !!geoapifyKey && needle.length >= 3) ||
            (mode === 'Features' && needle.length >= 2)),
      );
    });
    if (!open || mode === 'Coordinates') return;
    if (mode === 'Places' && (!geoapifyKey || needle.length < 3)) return;
    if (mode === 'Features' && needle.length < 2) return;
    const cacheKey = `${needle.toLocaleLowerCase()}|${centerRef.current.join(',')}`;
    if (mode === 'Places' && placeCache.current.has(cacheKey)) {
      queueMicrotask(() => {
        if (revision !== generation.current) return;
        setPlaces(placeCache.current.get(cacheKey) ?? []);
        setCompletedQuery(`Places:${needle}`);
        setLoading(false);
      });
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (revision !== generation.current) return;
      setLoading(true);
      if (mode === 'Places') {
        void fetch(geoapifyAutocompleteUrl(needle, geoapifyKey, centerRef.current), {
          signal: controller.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Geoapify returned ${response.status}`);
            return response.json() as Promise<unknown>;
          })
          .then((data) => {
            if (revision === generation.current) {
              const results = parseGeoapifyPlaces(data);
              placeCache.current.set(cacheKey, results);
              if (placeCache.current.size > 12)
                placeCache.current.delete(placeCache.current.keys().next().value!);
              setPlaces(results);
              setCompletedQuery(`Places:${needle}`);
            }
          })
          .catch(() => {
            if (revision === generation.current && !controller.signal.aborted)
              setError('Places search is unavailable. Try again.');
          })
          .finally(() => {
            if (revision === generation.current) setLoading(false);
          });
      } else {
        void searchProjectFeatures(sources, layers, needle, () => revision === generation.current)
          .then((result) => {
            if (revision === generation.current) {
              setFeatures(result.results);
              setHasMore(result.hasMore);
              setCompletedQuery(`Features:${needle}`);
            }
          })
          .catch(() => {
            if (revision === generation.current) setError('Feature search could not finish.');
          })
          .finally(() => {
            if (revision === generation.current) setLoading(false);
          });
      }
    }, 320);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      generation.current += 1;
    };
  }, [open, mode, query, sources, layers, geoapifyKey]);

  const close = () => {
    onDismiss();
    requestAnimationFrame(() => buttonRef.current?.focus());
  };
  const fitExtent = (extent: SpatialExtent) => {
    const frame = buttonRef.current?.closest<HTMLElement>('.map-viewport')?.getBoundingClientRect();
    return viewForExtent(extent, {
      ...(frame ? { viewportWidth: frame.width, viewportHeight: frame.height } : {}),
      padding: 48,
      maxZoom: 15,
    });
  };
  const pointView = (
    longitude: number,
    latitude: number,
    extent: PlaceResult['extent'],
  ): MapView =>
    extent && (extent.east - extent.west > 0.0001 || extent.north - extent.south > 0.0001)
      ? fitExtent(extent)
      : { center: [longitude, latitude], zoom: 14, bearing: 0, pitch: 0 };
  const choosePlace = (place: PlaceResult) => {
    showTarget({
      label: place.label,
      view: pointView(place.longitude, place.latitude, place.extent),
      geometry: { type: 'Point', coordinates: [place.longitude, place.latitude] },
    });
  };
  const chooseFeature = (feature: FeatureSearchResult) => {
    const extent = extentForGeometry(feature.geometry);
    if (!extent) return;
    showTarget({
      label: `${feature.label} · ${feature.layerName}`,
      view: fitExtent(extent),
      geometry: feature.geometry,
    });
  };
  const chooseCoordinate = () => {
    if (!coordinate) return;
    const { longitude, latitude } = coordinate;
    showTarget({
      label: `${latitude}, ${longitude}`,
      view: pointView(longitude, latitude, null),
      geometry: { type: 'Point', coordinates: [longitude, latitude] },
    });
  };
  const resultCount = mode === 'Places' ? places.length : features.length;
  const chooseActive = () => {
    if (mode === 'Coordinates') chooseCoordinate();
    else if (mode === 'Places' && places[activeIndex]) choosePlace(places[activeIndex]);
    else if (mode === 'Features' && features[activeIndex]) chooseFeature(features[activeIndex]);
  };
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!resultCount) return;
      event.preventDefault();
      setActiveIndex(
        (index) => (index + (event.key === 'ArrowDown' ? 1 : -1) + resultCount) % resultCount,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      chooseActive();
    }
  };

  return (
    <div className="map-search-control">
      <button
        aria-controls="map-search-panel"
        aria-expanded={open}
        aria-label="Search map"
        className="utility-button"
        onClick={onToggle}
        ref={buttonRef}
        title="Search map"
        type="button"
      >
        <Icon name="search" />
        <span>Search</span>
      </button>
      {open ? (
        <div
          aria-label="Map search"
          className="map-search-panel"
          id="map-search-panel"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
          ref={panelRef}
          role="group"
        >
          <div className="map-search-heading">
            <strong>Search map</strong>
            <button aria-label="Close search" onClick={close} type="button">
              ×
            </button>
          </div>
          <div aria-label="Search mode" className="map-search-modes" role="tablist">
            {modes.map((next) => (
              <button
                aria-controls="map-search-content"
                aria-selected={mode === next}
                className={mode === next ? 'active' : ''}
                id={`map-search-tab-${next.toLowerCase()}`}
                key={next}
                onClick={() => {
                  setMode(next);
                  setQuery('');
                  inputRef.current?.focus();
                }}
                onKeyDown={(event) => {
                  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                  event.preventDefault();
                  const index = modes.indexOf(next);
                  const target =
                    event.key === 'Home'
                      ? modes[0]
                      : event.key === 'End'
                        ? modes.at(-1)
                        : modes[
                            (index + (event.key === 'ArrowRight' ? 1 : -1) + modes.length) %
                              modes.length
                          ];
                  if (!target) return;
                  setMode(target);
                  setQuery('');
                  requestAnimationFrame(() =>
                    document.getElementById(`map-search-tab-${target.toLowerCase()}`)?.focus(),
                  );
                }}
                role="tab"
                type="button"
              >
                {next}
              </button>
            ))}
          </div>
          <div
            aria-labelledby={`map-search-tab-${mode.toLowerCase()}`}
            id="map-search-content"
            role="tabpanel"
          >
            <label htmlFor="map-search-input">
              {mode === 'Coordinates' ? 'Latitude, longitude' : `Search ${mode.toLowerCase()}`}
            </label>
            <input
              aria-activedescendant={resultCount ? `map-search-result-${activeIndex}` : undefined}
              aria-controls={mode === 'Coordinates' ? undefined : 'map-search-results'}
              aria-expanded={mode !== 'Coordinates' && resultCount > 0}
              autoComplete="off"
              id="map-search-input"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder={
                mode === 'Coordinates'
                  ? '-33.8688, 151.2093'
                  : mode === 'Features'
                    ? 'Name or attribute value'
                    : 'Address, city or place'
              }
              ref={inputRef}
              role={mode === 'Coordinates' ? undefined : 'combobox'}
              type="search"
              value={query}
            />
            {mode === 'Places' ? (
              <p className="map-search-attribution">
                Powered by{' '}
                <a href="https://www.geoapify.com/" rel="noreferrer" target="_blank">
                  Geoapify
                </a>{' '}
                ·{' '}
                <a href="https://www.openstreetmap.org/copyright" rel="noreferrer" target="_blank">
                  © OpenStreetMap contributors
                </a>
              </p>
            ) : null}
            {mode === 'Places' && !geoapifyKey ? (
              <p role="status">Places search is unavailable: Geoapify is not configured.</p>
            ) : null}
            {mode === 'Features' && layers.length === 0 ? (
              <p role="status">Add data to search features.</p>
            ) : null}
            {mode === 'Coordinates' && query.trim() ? (
              coordinate ? (
                <>
                  <p role="status">
                    Interpreted as {coordinate.order}: {coordinate.latitude}, {coordinate.longitude}
                  </p>
                  <button className="map-search-go" onClick={chooseCoordinate} type="button">
                    Go to coordinate
                  </button>
                </>
              ) : (
                <p role="status">Enter valid decimal degrees as latitude, longitude.</p>
              )
            ) : null}
            {loading ? <p role="status">Searching…</p> : null}
            {error ? <p role="alert">{error}</p> : null}
            {!loading &&
            !error &&
            completedQuery === `${mode}:${query.trim()}` &&
            ((mode === 'Places' && geoapifyKey && query.trim().length >= 3) ||
              (mode === 'Features' && query.trim().length >= 2)) &&
            resultCount === 0 ? (
              <p role="status">No results found.</p>
            ) : null}
            {resultCount > 0 ? (
              <ul aria-label={`${mode} results`} id="map-search-results" role="listbox">
                {mode === 'Places'
                  ? places.map((place, index) => (
                      <li
                        aria-selected={activeIndex === index}
                        id={`map-search-result-${index}`}
                        key={`${place.label}-${index}`}
                        onClick={() => choosePlace(place)}
                        onMouseEnter={() => setActiveIndex(index)}
                        role="option"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') choosePlace(place);
                        }}
                      >
                        {place.label}
                      </li>
                    ))
                  : features.map((feature, index) => (
                      <li
                        aria-selected={activeIndex === index}
                        id={`map-search-result-${index}`}
                        key={`${feature.layerId}-${feature.featureIndex}`}
                        onClick={() => chooseFeature(feature)}
                        onMouseEnter={() => setActiveIndex(index)}
                        role="option"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') chooseFeature(feature);
                        }}
                      >
                        <strong>{feature.label}</strong>
                        <small>
                          {feature.layerName} · {feature.match}
                        </small>
                      </li>
                    ))}
              </ul>
            ) : null}
            {hasMore ? (
              <p className="map-search-more">More matches exist. Refine your search.</p>
            ) : null}
            {chosen ? (
              <p className="map-search-chosen" role="status">
                Showing {chosen.label}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
